import { inject, Injectable, Injector, signal } from '@angular/core';
import { ClassicPreset, GetSchemes, NodeEditor } from 'rete';
import { AreaExtensions, AreaPlugin } from 'rete-area-plugin';
import {
  ConnectionPlugin,
  Presets as ConnectionPresets,
  type Connection as ConnectionSignal,
} from 'rete-connection-plugin';
import {
  AngularArea2D,
  AngularPlugin,
  Presets as AngularPresets,
} from 'rete-angular-plugin/21';
import { MouldingGraph } from '../../../models/moulding-item.model';
import { MouldingNodeComponent } from '../moulding-node/moulding-node.component';
import {
  getNodeDefinition,
  MOULDING_NODE_REGISTRY,
  NodeKind,
  NodeMeta,
  NodeRunStatus,
  NodeRuntimeDataByKind,
} from '../nodes/moulding-node-registry';

export type Schemes = GetSchemes<
  ClassicPreset.Node,
  ClassicPreset.Connection<ClassicPreset.Node, ClassicPreset.Node>
>;
export type AreaExtra = AngularArea2D<Schemes> | ConnectionSignal;
export type MouldingViewport = { x: number; y: number; k: number };

const DEFAULT_VIEWPORT: MouldingViewport = { x: 0, y: 0, k: 1 };
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.1;

/**
 * Owns the Rete editor and its graph state for one Moulding page.
 *
 * This service creates, renders, serializes, and mutates graph topology and
 * node runtime data. Flow-specific processing, API calls, and WebSocket state
 * are coordinated separately by MouldingProcessingService.
 */
@Injectable()
export class MouldingGraphService {
  private readonly injector = inject(Injector);

  readonly socket = new ClassicPreset.Socket('flow');
  readonly nodeMeta = new Map<string, NodeMeta>();

  editor: NodeEditor<Schemes> | null = null;
  area: AreaPlugin<Schemes, AreaExtra> | null = null;

  readonly viewport = signal<MouldingViewport>(DEFAULT_VIEWPORT);
  onChange: (() => void) | null = null;
  onViewportChange: (() => void) | null = null;
  private isApplyingGraph = false;

  async init(container: HTMLElement): Promise<void> {
    await this.destroy();
    container.innerHTML = '';

    const editor = new NodeEditor<Schemes>();
    const area = new AreaPlugin<Schemes, AreaExtra>(container);
    const connection = new ConnectionPlugin<Schemes, AreaExtra>();
    const render = new AngularPlugin<Schemes, AreaExtra>({
      injector: this.injector,
    });

    editor.use(area);
    area.use(connection);
    area.use(render);
    connection.addPreset(ConnectionPresets.classic.setup());
    render.addPreset(
      AngularPresets.classic.setup({
        customize: {
          node: () => MouldingNodeComponent,
        },
      }),
    );
    AreaExtensions.simpleNodesOrder(area);
    AreaExtensions.restrictor(area, {
      scaling: { min: MIN_ZOOM, max: MAX_ZOOM },
    });

    editor.addPipe((context) => {
      if (
        context.type === 'connectioncreated' ||
        context.type === 'connectionremoved' ||
        context.type === 'nodecreated' ||
        context.type === 'noderemoved'
      ) {
        if (context.type === 'noderemoved') {
          this.nodeMeta.delete(String(context.data.id));
        }
        if (!this.isApplyingGraph) {
          this.onChange?.();
          // Node views use detached change detection. A topology change can
          // change another node's start eligibility, so refresh after Rete has
          // applied this signal rather than leaving its disabled button stale.
          queueMicrotask(() => void this.refreshAllNodes());
        }
      }
      return context;
    });

    area.addPipe((context) => {
      if (!this.isApplyingGraph && context.type === 'nodetranslated') {
        this.onChange?.();
      }
      if (
        !this.isApplyingGraph &&
        (context.type === 'zoomed' || context.type === 'translated')
      ) {
        this.syncViewport();
        this.onViewportChange?.();
      }
      return context;
    });
    area.addPipe(async (context) => {
      if (context.type === 'rendered' && context.data.type === 'node') {
        const { element, payload } = context.data;
        const { clientWidth, clientHeight } = element;
        if (clientWidth > 0 && clientHeight > 0) {
          // Angular mounts node content asynchronously. Rete otherwise keeps
          // stale node bounds, so a drag can miss the node handler and fall
          // through to the canvas-pan handler. Do not remove this sync unless
          // node measurement is moved into the renderer itself.
          await area.resize(payload.id, clientWidth, clientHeight);
        }
      }
      return context;
    });

    this.editor = editor;
    this.area = area;
  }

  async destroy(): Promise<void> {
    this.area?.destroy();
    this.area = null;
    this.editor = null;
    this.nodeMeta.clear();
    this.viewport.set(DEFAULT_VIEWPORT);
  }

  getZoom(): number {
    return this.viewport().k;
  }

  get canZoomIn(): boolean {
    return this.getZoom() < MAX_ZOOM;
  }

  get canZoomOut(): boolean {
    return this.getZoom() > MIN_ZOOM;
  }

  async zoomIn(): Promise<void> {
    await this.setZoom(Math.min(MAX_ZOOM, this.getZoom() + ZOOM_STEP));
  }

  async zoomOut(): Promise<void> {
    await this.setZoom(Math.max(MIN_ZOOM, this.getZoom() - ZOOM_STEP));
  }

  async setZoom(zoom: number): Promise<void> {
    const area = this.area;
    const container = area?.container;
    if (!area || !container || !Number.isFinite(zoom)) {
      return;
    }

    const normalizedZoom = Math.min(
      MAX_ZOOM,
      Math.max(MIN_ZOOM, Math.round(zoom * 100) / 100),
    );
    const currentZoom = area.area.transform.k;
    const delta = normalizedZoom / currentZoom - 1;
    if (delta === 0) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const holderRect = area.area.content.holder.getBoundingClientRect();
    const pivotX = containerRect.left + containerRect.width / 2;
    const pivotY = containerRect.top + containerRect.height / 2;
    const offsetX = (holderRect.left - pivotX) * delta;
    const offsetY = (holderRect.top - pivotY) * delta;

    await area.area.zoom(normalizedZoom, offsetX, offsetY);
  }

  async addNode(kind: NodeKind): Promise<void> {
    const editor = this.editor;
    const area = this.area;
    if (!editor || !area) {
      return;
    }

    const node = this.createNodeByKind(kind);
    await editor.addNode(node);
    await area.translate(node.id, this.getViewportCenterPosition());
    await area.update('node', node.id);
  }

  private getViewportCenterPosition(): { x: number; y: number } {
    const viewportCenter = this.getViewportCenter();
    const nodeWidth = 280;
    const nodeHeight = 160;

    return {
      x: viewportCenter.x - nodeWidth / 2,
      y: viewportCenter.y - nodeHeight / 2,
    };
  }

  private getViewportCenter(): { x: number; y: number } {
    const container = this.area?.container;
    const transform = this.area?.area.transform;
    if (!container || !transform) {
      return { x: 0, y: 0 };
    }

    const { width, height } = container.getBoundingClientRect();
    return {
      x: (width / 2 - transform.x) / transform.k,
      y: (height / 2 - transform.y) / transform.k,
    };
  }

  getNodeKinds(): NodeKind[] {
    return Object.keys(MOULDING_NODE_REGISTRY) as NodeKind[];
  }

  getNodeLabel(kind: NodeKind): string {
    return getNodeDefinition(kind).label;
  }

  getNodeIcon(kind: NodeKind): string {
    return getNodeDefinition(kind).icon;
  }

  getNodeKind(nodeId: string): NodeKind | null {
    return this.nodeMeta.get(nodeId)?.kind ?? null;
  }

  getNodeSummary(nodeId: string): string {
    const meta = this.nodeMeta.get(nodeId);
    if (!meta) {
      return 'Unknown node';
    }
    return getNodeDefinition(meta.kind).describe(
      meta.data as NodeRuntimeDataByKind[typeof meta.kind],
    );
  }

  async updateNodeName(nodeId: string, name: string): Promise<void> {
    const editor = this.editor;
    const node = editor
      ?.getNodes()
      .find((candidate) => String(candidate.id) === nodeId);
    const kind = this.getNodeKind(nodeId);
    if (!node || !kind) {
      return;
    }

    const defaultName = getNodeDefinition(kind).label;
    const nextName = name.trim() || defaultName;
    if (node.label === nextName) {
      return;
    }

    node.label = nextName;
    await this.area?.update('node', node.id);
    this.onChange?.();
  }

  isNodeOutdated(nodeId: string): boolean {
    const meta = this.nodeMeta.get(nodeId);
    if (!meta || !meta.data.isOutdated) {
      return false;
    }
    const status = this.getNodeRunStatus(meta);
    return status === 'complete' || status === 'error';
  }

  getNodeData<K extends NodeKind>(
    nodeId: string,
    expectedKind: K,
  ): NodeRuntimeDataByKind[K] | null {
    const meta = this.nodeMeta.get(nodeId);
    if (!meta || meta.kind !== expectedKind) {
      return null;
    }
    return meta.data as NodeRuntimeDataByKind[K];
  }

  updateNodeData<K extends NodeKind>(
    nodeId: string,
    expectedKind: K,
    patch: Partial<NodeRuntimeDataByKind[K]>,
  ): void {
    const meta = this.nodeMeta.get(nodeId);
    if (!meta || meta.kind !== expectedKind) {
      return;
    }
    const currentData = meta.data as NodeRuntimeDataByKind[K];
    const nextData = {
      ...currentData,
      ...patch,
    } as NodeRuntimeDataByKind[K];
    if (JSON.stringify(currentData) === JSON.stringify(nextData)) {
      return;
    }

    const definition = getNodeDefinition(expectedKind);
    const persistedDataChanged =
      JSON.stringify(definition.serializeData(currentData)) !==
      JSON.stringify(definition.serializeData(nextData));

    meta.data = nextData as NodeRuntimeDataByKind[typeof meta.kind];
    if (persistedDataChanged) {
      this.onChange?.();
    }
    // CAD/direction updates affect connected processing nodes too. Their
    // templates are detached from Angular's normal change-detection tree, so
    // updating only the source node leaves Start Step disabled until another
    // action (such as opening Configure) manually detects changes.
    void this.refreshAllNodes();
  }

  private createNodeByKind(kind: NodeKind, id?: string): Schemes['Node'] {
    const definition = getNodeDefinition(kind);
    const node = new ClassicPreset.Node(definition.label);
    if (id) {
      node.id = id;
    }

    for (const input of definition.inputs) {
      node.addInput(
        input.key,
        new ClassicPreset.Input(this.socket, input.label),
      );
    }
    for (const output of definition.outputs) {
      node.addOutput(
        output.key,
        new ClassicPreset.Output(this.socket, output.label),
      );
    }

    this.nodeMeta.set(String(node.id), {
      kind,
      data: definition.createInitialData(),
    } as NodeMeta);
    return node;
  }

  async deleteNode(nodeId: string): Promise<void> {
    const editor = this.editor;
    if (!editor) {
      return;
    }

    const node = editor
      .getNodes()
      .find((candidate) => String(candidate.id) === nodeId);
    if (!node) {
      return;
    }

    const connectionsToRemove = editor
      .getConnections()
      .filter(
        (connection) =>
          String(connection.source) === nodeId ||
          String(connection.target) === nodeId,
      );
    for (const connection of connectionsToRemove) {
      await editor.removeConnection(connection.id);
    }

    await editor.removeNode(node.id);
  }

  async clearGraph(): Promise<void> {
    const editor = this.editor;
    if (!editor) {
      return;
    }

    const connectionIds = editor
      .getConnections()
      .map((connection) => connection.id);
    for (const id of connectionIds) {
      await editor.removeConnection(id);
    }
    const nodeIds = editor.getNodes().map((node) => node.id);
    for (const id of nodeIds) {
      await editor.removeNode(id);
    }
  }

  serialize(): MouldingGraph {
    const editor = this.editor;
    const area = this.area;
    if (!editor || !area) {
      return { nodes: [], connections: [] };
    }

    const nodes = editor.getNodes().map((node) => {
      const id = String(node.id);
      const meta = this.nodeMeta.get(id);
      const position = area.nodeViews.get(id)?.position ?? { x: 0, y: 0 };
      const kind: NodeKind = meta?.kind ?? 'pull_direction';
      const definition = getNodeDefinition(kind);
      return {
        id,
        kind,
        position: { x: position.x, y: position.y },
        ...(node.label !== definition.label ? { name: node.label } : {}),
        ...(meta
          ? definition.serializeData(
              meta.data as NodeRuntimeDataByKind[typeof kind],
            )
          : {}),
      };
    });

    const connections = editor.getConnections().map((connection) => ({
      source: String(connection.source),
      sourceOutput: connection.sourceOutput,
      target: String(connection.target),
      targetInput: connection.targetInput,
    }));

    return { nodes, connections, viewport: this.viewport() };
  }

  async loadGraph(graph: MouldingGraph): Promise<void> {
    const editor = this.editor;
    const area = this.area;
    if (!editor || !area) {
      return;
    }
    this.isApplyingGraph = true;
    try {
      await this.clearGraph();
      await this.materializeGraph(graph);
      await this.refreshAllNodes();
      await this.restoreViewport(graph.viewport);
    } finally {
      this.isApplyingGraph = false;
    }

    this.syncViewport();
  }

  /**
   * Adds an imported, self-contained graph without changing existing nodes or
   * their runtime state. Callers must remap source-flow node IDs first.
   */
  async appendGraph(graph: MouldingGraph): Promise<void> {
    const editor = this.editor;
    const area = this.area;
    if (!editor || !area || graph.nodes.length === 0) {
      return;
    }

    const positionedGraph = this.positionGraphForAppend(graph);
    const addedNodeIds: Schemes['Node']['id'][] = [];
    const addedConnectionIds: Schemes['Connection']['id'][] = [];

    this.isApplyingGraph = true;
    try {
      await this.materializeGraph(
        positionedGraph,
        addedNodeIds,
        addedConnectionIds,
      );
    } catch (error) {
      for (const connectionId of addedConnectionIds.reverse()) {
        await editor.removeConnection(connectionId);
      }
      for (const nodeId of addedNodeIds.reverse()) {
        await editor.removeNode(nodeId);
      }
      throw error;
    } finally {
      this.isApplyingGraph = false;
    }

    await this.refreshAllNodes();
    this.onChange?.();
  }

  private async restoreViewport(
    viewport: { x: number; y: number; k: number } | undefined,
  ): Promise<void> {
    const area = this.area;
    if (!area) {
      return;
    }

    const { x, y, k } = viewport ?? { x: 0, y: 0, k: 1 };
    await area.area.zoom(k);
    await area.area.translate(x, y);
  }

  private syncViewport(): void {
    const transform = this.area?.area.transform;
    this.viewport.set(transform ? { ...transform } : DEFAULT_VIEWPORT);
  }

  private async materializeGraph(
    graph: MouldingGraph,
    addedNodeIds: Schemes['Node']['id'][] = [],
    addedConnectionIds: Schemes['Connection']['id'][] = [],
  ): Promise<void> {
    const editor = this.editor;
    const area = this.area;
    if (!editor || !area) {
      return;
    }

    const idMap = new Map<string, Schemes['Node']>();
    for (const serialized of graph.nodes) {
      const node = this.createNodeByKind(serialized.kind, serialized.id);
      node.label = serialized.name ?? getNodeDefinition(serialized.kind).label;
      const nodeMeta = this.nodeMeta.get(String(node.id));
      if (nodeMeta) {
        const definition = getNodeDefinition(serialized.kind);
        nodeMeta.data = definition.hydrateData(
          serialized,
        ) as NodeRuntimeDataByKind[typeof serialized.kind];
      }
      if (!(await editor.addNode(node))) {
        this.nodeMeta.delete(String(node.id));
        throw new Error(
          `Unable to add imported node "${serialized.name ?? node.label}".`,
        );
      }
      addedNodeIds.push(node.id);
      await area.translate(node.id, {
        x: serialized.position.x,
        y: serialized.position.y,
      });
      idMap.set(serialized.id, node);
    }

    for (const connection of graph.connections) {
      const source = idMap.get(connection.source);
      const target = idMap.get(connection.target);
      if (!source || !target) {
        throw new Error(
          'Imported graph contains a connection to a missing node.',
        );
      }
      const reteConnection = new ClassicPreset.Connection(
        source,
        connection.sourceOutput,
        target,
        connection.targetInput,
      );
      if (!(await editor.addConnection(reteConnection))) {
        throw new Error('Unable to add an imported connection.');
      }
      addedConnectionIds.push(reteConnection.id);
    }
  }

  private positionGraphForAppend(graph: MouldingGraph): MouldingGraph {
    const importedLeft = Math.min(
      ...graph.nodes.map((node) => node.position.x),
    );
    const importedRight = Math.max(
      ...graph.nodes.map((node) => node.position.x),
    );
    const importedTop = Math.min(...graph.nodes.map((node) => node.position.y));
    const importedBottom = Math.max(
      ...graph.nodes.map((node) => node.position.y),
    );
    const viewportCenter = this.getViewportCenter();
    const offsetX = viewportCenter.x - (importedLeft + importedRight) / 2;
    const offsetY = viewportCenter.y - (importedTop + importedBottom) / 2;

    return {
      ...graph,
      nodes: graph.nodes.map((node) => ({
        ...node,
        position: {
          x: node.position.x + offsetX,
          y: node.position.y + offsetY,
        },
      })),
    };
  }

  async refreshAllNodes(): Promise<void> {
    const editor = this.editor;
    if (!editor) {
      return;
    }
    for (const node of editor.getNodes()) {
      await this.area?.update('node', node.id);
    }
  }

  private getNodeRunStatus(meta: NodeMeta): NodeRunStatus {
    return meta.data.status;
  }
}
